import React, { useState, useEffect } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  InputAdornment
} from '@mui/material';
import { Search, Edit, Save, Delete, Check, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import { useSucursal } from '../contexts/SucursalContext';
import { useAccesos } from '../contexts/AccesosContext';

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdministracionResultados = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [editingRows, setEditingRows] = useState({});
  const [orderBy, setOrderBy] = useState('FechaRecepcion');
  const [order, setOrder] = useState('desc');

  // Define separate states for each table
  const [resultadosBt, setResultadosBt] = useState([]);
  const [resultadosCm250, setResultadosCm250] = useState([]);
  const [resultadosCobasPure, setResultadosCobasPure] = useState([]);
  const [resultadosHematologium, setResultadosHematologium] = useState([]);
  const [resultadosI400, setResultadosI400] = useState([]);

  const { user } = useUser();
  const { sucursalSeleccionada } = useSucursal();
  const { tienePermiso } = useAccesos();
  const server = process.env.REACT_APP_API_ROUTE;
  const key = process.env.REACT_APP_API_KEY;

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);  // Reset page when changing tabs
    setSearchTerm('');  // Reset search
    setEditingRows({});  // Cancel any editing
    setOrderBy('FechaRecepcion');  // Reset sorting to FechaRecepcion
    setOrder('desc');
  };

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);  // Reset to first page when sorting
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Toggle edit mode for a row
  const toggleEditMode = (id) => {
    setEditingRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  // Show snackbar notification
  const showNotification = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Format date to yyyy-MM-dd HH:mm:ss
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Handle input change for cell editing
  const handleInputChange = (id, field, value, setter, currentItems) => {
    const updatedItems = currentItems.map(item => {
      if (item.Identificador === id) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setter(updatedItems);
  };
  // Update a result record
  const updateResult = async (id, data, apiEndpoint, setter, currentItems) => {
    try {
      // Asegurar que siempre se envíe el campo ModificadoPor
      const dataToSend = {
        ...data,
        ModificadoPor: user?.usuario || user?.codigoUsuario || 'sistema'
      };

      const response = await fetch(`${server}/${apiEndpoint}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': key
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        showNotification('Registro actualizado correctamente', 'success');
        toggleEditMode(id);
        fetchData(); // Refresh data
      } else {
        const errorData = await response.json();
        showNotification(`Error al actualizar: ${errorData.message || 'Error desconocido'}`, 'error');
      }
    } catch (error) {
      showNotification(`Error de conexión: ${error.message}`, 'error');
    }
  };
  
  // Delete a result record
  const deleteResult = async (id, apiEndpoint, setter, currentItems) => {
    try {
      // Confirmación primero
      const result = await Swal.fire({
        title: '¿Eliminar registro?',
        text: "Esta acción no se puede revertir",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        // Asegurarnos de tener siempre un usuario para la auditoría
        const usuarioAuditoria = user?.usuario || user?.codigoUsuario || 'sistema';
        
        const response = await fetch(`${server}/${apiEndpoint}/${id}/${usuarioAuditoria}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'apikey': key
          }
        });

        if (response.ok) {          // Actualizar el estado eliminando el registro
          const updatedItems = currentData.filter(item => item.Identificador !== id);
          setter(updatedItems);
          showNotification('Registro eliminado correctamente', 'success');
        } else {
          const errorData = await response.json();
          showNotification(`Error al eliminar: ${errorData.message || 'Error desconocido'}`, 'error');
        }
      }
    } catch (error) {
      showNotification(`Error de conexión: ${error.message}`, 'error');
    }
  };

  // Save edited row  
  const saveRow = (item, apiEndpoint, setter, currentData) => {
    const id = item.Identificador;
    const data = { ...item };
    updateResult(id, data, apiEndpoint, setter, currentData);
  };
  // Filter, sort and paginate data
  const getFilteredData = (data) => {
    // Ensure data is an array before attempting to filter
    const dataArray = Array.isArray(data) ? data : [];
    console.log('getFilteredData - Datos recibidos:', dataArray);
    console.log('getFilteredData - Es array:', Array.isArray(dataArray));
    console.log('getFilteredData - Longitud:', dataArray.length);
    
    if (dataArray.length === 0) {
      console.log('No hay datos para filtrar/paginar');
      return [];
    }
    
    // Ejemplo del primer item para ver su estructura
    if (dataArray.length > 0) {
      console.log('Ejemplo de item:', dataArray[0]);
    }
    
    const filtered = dataArray.filter(item => {
      if (!searchTerm) return true;
      
      // Asegurarnos de que el item exista
      if (!item) return false;
      
      // Buscar en varios campos, asegurándonos de manejar valores undefined
      const idOrden = (item.IdOrden || '').toString().toLowerCase();
      const resultado = (item.Resultado || '').toString().toLowerCase();
      const examen = (item.Examen || '').toString().toLowerCase();
      
      const searchTermLower = searchTerm.toLowerCase();
      
      return idOrden.includes(searchTermLower) || 
             resultado.includes(searchTermLower) || 
             examen.includes(searchTermLower);
    });
    
    console.log('Datos después de filtrar:', filtered.length);
    
    // Apply sorting if orderBy is set
    let sorted = [...filtered];
    if (orderBy) {
      sorted = filtered.sort((a, b) => {
        let aVal = a[orderBy];
        let bVal = b[orderBy];
        
        // Handle null/undefined values
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';
        
        // Handle dates
        if (orderBy.includes('Fecha')) {
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
        }
        // Handle numbers
        else if (typeof aVal === 'number' || typeof bVal === 'number') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        }
        // Handle booleans
        else if (typeof aVal === 'boolean' || typeof bVal === 'boolean') {
          aVal = aVal ? 1 : 0;
          bVal = bVal ? 1 : 0;
        }
        // Handle strings
        else {
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }
        
        if (order === 'asc') {
          return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        } else {
          return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
        }
      });
    }
    
    console.log('Datos después de ordenar:', sorted.length);
    
    const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    console.log('Datos después de paginar:', paginated.length);
    
    return paginated;
  };
  // Fetch data based on current tab
  const fetchData = async () => {
    try {
      console.log('Iniciando fetchData para tab:', tabValue);
      console.log('API Server:', server);
      console.log('API Key disponible:', !!key);

      // Definir endpoints para cada tabla
      const endpoints = [
        'SsyrResultadosCm250',
        'SsyrResultadosCobasPure',
        'SsyrResultadosHematologium'
      ];
      
      // Get data based on current tab
      const endpoint = endpoints[tabValue];
      console.log('Endpoint seleccionado:', endpoint);
      
      const url = `${server}/${endpoint}`;
      console.log('URL completa:', url);
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'apikey': key
        }
      });      if (response.ok) {
        const data = await response.json();
        console.log(`Datos obtenidos de ${endpoint}:`, data);
        
        // Verificar la estructura exacta de la respuesta
        console.log('Tipo de datos recibidos:', typeof data);
        
        // Variable para almacenar los datos procesados
        let safeData = [];
        
        // Manejar el formato específico de JSON.NET con $id y $values
        if (data && data.$values && Array.isArray(data.$values)) {
          console.log('Encontrada estructura con $values:', data.$values.length, 'elementos');
          
          // Mapear los datos con las propiedades correctamente formateadas
          safeData = data.$values.map(item => {
            return {
              Identificador: item.identificador,
              IdOrden: item.idOrden,
              Examen: item.examen,
              AliasAnalisis: item.aliasAnalisis,
              Muestra: item.muestra,
              Resultado: item.resultado,
              FechaRecepcion: item.fechaRecepcion,
              FechaProcesado: item.fechaProcesado,
              Procesado: item.procesado === true,
              Sincronizado: item.sincronizado === true,
              SincronizadoFechaHora: item.sincronizadoFechaHora,
              SincronizadoIdEncabezado: item.sincronizadoIdEncabezado,
              IdExamen: item.idExamen,
              GlobulosBlancos: item.globulosBlancos,
              GlobulosRojos: item.globulosRojos,
              Hemoglobina: item.hemoglobina
            };
          });
        } 
        else if (Array.isArray(data)) {
          console.log('Dato es ya un Array, mapeando propiedades');
          safeData = data.map(item => {
            return {
              Identificador: item.identificador || item.Identificador,
              IdOrden: item.idOrden || item.IdOrden,
              Examen: item.examen || item.Examen,
              AliasAnalisis: item.aliasAnalisis || item.AliasAnalisis,
              Muestra: item.muestra || item.Muestra,
              Resultado: item.resultado || item.Resultado,
              FechaRecepcion: item.fechaRecepcion || item.FechaRecepcion,
              FechaProcesado: item.fechaProcesado || item.FechaProcesado,
              Procesado: item.procesado === true || item.Procesado === true,
              Sincronizado: item.sincronizado === true || item.Sincronizado === true,
              SincronizadoFechaHora: item.sincronizadoFechaHora || item.SincronizadoFechaHora,
              SincronizadoIdEncabezado: item.sincronizadoIdEncabezado || item.SincronizadoIdEncabezado,
              IdExamen: item.idExamen || item.IdExamen,
              GlobulosBlancos: item.globulosBlancos || item.GlobulosBlancos,
              GlobulosRojos: item.globulosRojos || item.GlobulosRojos,
              Hemoglobina: item.hemoglobina || item.Hemoglobina
            };
          });
        }
        // Caso específico para la estructura JSON que vemos en la captura
        else if (data && data.$id && data.$values) {
          console.log('Estructura JSON.NET detectada con $id y $values');
          safeData = data.$values.map(item => {
            return {
              Identificador: item.identificador,
              IdOrden: item.idOrden,
              Examen: item.examen,
              AliasAnalisis: item.aliasAnalisis,
              Muestra: item.muestra,
              Resultado: item.resultado,
              FechaRecepcion: item.fechaRecepcion,
              FechaProcesado: item.fechaProcesado,
              Procesado: item.procesado === true,
              Sincronizado: item.sincronizado === true,
              SincronizadoFechaHora: item.sincronizadoFechaHora,
              SincronizadoIdEncabezado: item.sincronizadoIdEncabezado,
              IdExamen: item.idExamen,
              GlobulosBlancos: item.globulosBlancos,
              GlobulosRojos: item.globulosRojos,
              Hemoglobina: item.hemoglobina
            };
          });
        }
        
        console.log('Datos procesados finales:', safeData.length, 'elementos');
        if (safeData.length > 0) {
          console.log('Muestra del primer elemento procesado:', safeData[0]);
        }
        
        // Actualizar el estado correspondiente
        switch (tabValue) {
          case 0:
            setResultadosCm250(safeData);
            break;
          case 1:
            setResultadosCobasPure(safeData);
            break;
          case 2:
            setResultadosHematologium(safeData);
            break;
          default:
            break;
        }
      } else {
        showNotification('Error al cargar los datos', 'error');
      }
    } catch (error) {
      showNotification(`Error de conexión: ${error.message}`, 'error');
    }
  };
  // Fetch data when tab changes
  useEffect(() => {
    console.log('Tab changed to:', tabValue);
    
    // Verificar que tenemos las configuraciones necesarias
    if (!server) {
      console.error('No se ha configurado la URL del servidor API (REACT_APP_API_ROUTE)');
      showNotification('Error: No se ha configurado la URL del servidor API', 'error');
      return;
    }
    
    if (!key) {
      console.error('No se ha configurado la API KEY (REACT_APP_API_KEY)');
      showNotification('Error: No se ha configurado la API KEY', 'error');
      return;
    }
    
    fetchData();
  }, [tabValue, server, key]);

  // Añadir useEffect para monitorear los datos
  useEffect(() => {
    const dataArrays = [resultadosBt, resultadosCm250, resultadosCobasPure, resultadosHematologium, resultadosI400];
    const tabNames = ['BT', 'CM250', 'COBAS PURE', 'HEMATOLOGIUM', 'I400'];
    
    dataArrays.forEach((data, index) => {
      console.log(`Estado datos ${tabNames[index]}:`, data);
      console.log(`Es array: ${Array.isArray(data)}, Longitud: ${Array.isArray(data) ? data.length : 'N/A'}`);
      
      // Mostrar el primer elemento para verificar la estructura
      if (Array.isArray(data) && data.length > 0) {
        console.log(`Primer elemento de ${tabNames[index]}:`, data[0]);
        
        // Verificar si las propiedades están accesibles (en mayúsculas)
        const firstItem = data[0];
        console.log(`Propiedades accesibles en ${tabNames[index]}:`, {
          Identificador: firstItem.Identificador,
          IdOrden: firstItem.IdOrden,
          Examen: firstItem.Examen,
          Resultado: firstItem.Resultado
        });
      }
    });
  }, [resultadosBt, resultadosCm250, resultadosCobasPure, resultadosHematologium, resultadosI400]);

  // Render the appropriate table based on the current tab
  const renderTable = () => {
    let currentData = [];
    let apiEndpoint = '';
    let setter = null;
    let isHematologium = false;

    switch (tabValue) {
      case 0:
        currentData = Array.isArray(resultadosCm250) ? resultadosCm250 : [];
        apiEndpoint = 'SsyrResultadosCm250';
        setter = setResultadosCm250;
        break;
      case 1:
        currentData = Array.isArray(resultadosCobasPure) ? resultadosCobasPure : [];
        apiEndpoint = 'SsyrResultadosCobasPure';
        setter = setResultadosCobasPure;
        break;
      case 2:        
        currentData = Array.isArray(resultadosHematologium) ? resultadosHematologium : [];
        apiEndpoint = 'SsyrResultadosHematologium';
        setter = setResultadosHematologium;
        isHematologium = true;
        break;
      default:
        break;
    }

    console.log("Current tab data:", currentData);
    
    // Debug: Revisar el primer elemento en detalle si existe
    if (currentData.length > 0) {
      const firstItem = currentData[0];
      console.log("Primer elemento en detalle:", {
        Identificador: firstItem.Identificador,
        IdOrden: firstItem.IdOrden,
        Examen: firstItem.Examen,
        Muestra: firstItem.Muestra,
        Resultado: firstItem.Resultado,
        FechaRecepcion: firstItem.FechaRecepcion,
        Procesado: firstItem.Procesado,
        Sincronizado: firstItem.Sincronizado
      });
    }
    
    const filteredData = getFilteredData(currentData);
    console.log("Filtered data:", filteredData);

    // Make sure we handle the TablePagination count safely
    const getFilteredCount = (data) => {
      if (!Array.isArray(data)) return 0;
      
      return data.filter(item => {
        if (!searchTerm) return true;
        const idOrden = item.IdOrden?.toLowerCase() || '';
        const resultado = item.Resultado?.toLowerCase() || '';
        const examen = item.Examen?.toLowerCase() || '';
        return idOrden.includes(searchTerm.toLowerCase()) ||
               resultado.includes(searchTerm.toLowerCase()) ||
               examen.includes(searchTerm.toLowerCase());
      }).length;
    };

    // Helper to render sortable header cell
    const SortableTableCell = ({ field, label }) => (
      <TableCell 
        onClick={() => handleRequestSort(field)}
        sx={{ 
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          {orderBy === field && (
            order === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
          )}
        </Box>
      </TableCell>
    );

    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTableCell field="Identificador" label="Identificador" />
              <SortableTableCell field="IdOrden" label="ID Orden" />
              {isHematologium ? (
                <>
                  <SortableTableCell field="GlobulosBlancos" label="Glóbulos Blancos" />
                  <SortableTableCell field="GlobulosRojos" label="Glóbulos Rojos" />
                  <SortableTableCell field="Hemoglobina" label="Hemoglobina" />
                </>
              ) : (
                <>
                  <SortableTableCell field="Examen" label="Examen" />
                  <SortableTableCell field="Muestra" label="Muestra" />
                  <SortableTableCell field="Resultado" label="Resultado" />
                </>
              )}
              <SortableTableCell field="FechaRecepcion" label="Fecha Recepción" />
              <SortableTableCell field="IdExamen" label="ID Examen" />
              <SortableTableCell field="Procesado" label="Procesado" />
              <SortableTableCell field="FechaProcesado" label="Fecha Procesado" />
              <SortableTableCell field="Sincronizado" label="Sincronizado" />
              <SortableTableCell field="SincronizadoFechaHora" label="Fecha Sinc." />
              <SortableTableCell field="SincronizadoIdEncabezado" label="ID Encabezado" />
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>          <TableBody>            {filteredData && filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <TableRow key={item?.Identificador || `row-${index}`}>
                  <TableCell>
                    {item.Identificador || ''}
                  </TableCell>
                  <TableCell>
                    {editingRows[item.Identificador] ? (
                      <TextField 
                        size="small"
                        value={item.IdOrden || ''}
                        onChange={(e) => handleInputChange(item.Identificador, 'IdOrden', e.target.value, setter, currentData)}
                      />
                    ) : (
                      item.IdOrden || ''
                    )}
                  </TableCell>
                  
                  {isHematologium ? (
                    // Campos específicos para Hematologium
                    <>
                      <TableCell>
                        {editingRows[item.Identificador] ? (
                          <TextField 
                            size="small"
                            value={item.GlobulosBlancos || ''}
                            onChange={(e) => handleInputChange(item.Identificador, 'GlobulosBlancos', e.target.value, setter, currentData)}
                          />
                        ) : (
                          item.GlobulosBlancos || ''
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRows[item.Identificador] ? (
                          <TextField 
                            size="small"
                            value={item.GlobulosRojos || ''}
                            onChange={(e) => handleInputChange(item.Identificador, 'GlobulosRojos', e.target.value, setter, currentData)}
                          />
                        ) : (
                          item.GlobulosRojos || ''
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRows[item.Identificador] ? (
                          <TextField 
                            size="small"
                            value={item.Hemoglobina || ''}
                            onChange={(e) => handleInputChange(item.Identificador, 'Hemoglobina', e.target.value, setter, currentData)}
                          />
                        ) : (
                          item.Hemoglobina || ''
                        )}
                      </TableCell>
                    </>
                  ) : (
                    // Campos para las otras tablas
                    <>
                      <TableCell>
                        {editingRows[item.Identificador] ? (
                          <TextField 
                            size="small"
                            value={item.Examen || ''}
                            onChange={(e) => handleInputChange(item.Identificador, 'Examen', e.target.value, setter, currentData)}
                          />
                        ) : (
                          item.Examen || ''
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRows[item.Identificador] ? (
                          <TextField 
                            size="small"
                            value={item.Muestra || ''}
                            onChange={(e) => handleInputChange(item.Identificador, 'Muestra', e.target.value, setter, currentData)}
                          />
                        ) : (
                          item.Muestra || ''
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRows[item.Identificador] ? (
                          <TextField 
                            size="small"
                            value={item.Resultado || ''}
                            onChange={(e) => handleInputChange(item.Identificador, 'Resultado', e.target.value, setter, currentData)}
                          />
                        ) : (
                          item.Resultado || ''
                        )}
                      </TableCell>
                    </>
                  )}
                  
                  <TableCell>
                    {formatDateTime(item.FechaRecepcion)}
                  </TableCell>
                  <TableCell>
                    {editingRows[item.Identificador] ? (
                      <TextField 
                        size="small"
                        type="number"
                        value={item.IdExamen || ''}
                        onChange={(e) => handleInputChange(item.Identificador, 'IdExamen', e.target.value, setter, currentData)}
                        sx={{ width: '80px' }}
                      />
                    ) : (
                      item.IdExamen || ''
                    )}
                  </TableCell>
                  <TableCell>
                    {editingRows[item.Identificador] ? (
                      <Checkbox
                        checked={item.Procesado || false}
                        onChange={(e) => handleInputChange(item.Identificador, 'Procesado', e.target.checked, setter, currentData)}
                      />
                    ) : (
                      item.Procesado ? <Check color="primary"/> : ''
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDateTime(item.FechaProcesado)}
                  </TableCell>
                  <TableCell>
                    {editingRows[item.Identificador] ? (
                      <Checkbox
                        checked={item.Sincronizado || false}
                        onChange={(e) => handleInputChange(item.Identificador, 'Sincronizado', e.target.checked, setter, currentData)}
                      />
                    ) : (
                      item.Sincronizado ? <Check color="primary"/> : ''
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDateTime(item.SincronizadoFechaHora)}
                  </TableCell>
                  <TableCell>
                    {editingRows[item.Identificador] ? (
                      <TextField 
                        size="small"
                        type="number"
                        value={item.SincronizadoIdEncabezado || ''}
                        onChange={(e) => handleInputChange(item.Identificador, 'SincronizadoIdEncabezado', e.target.value, setter, currentData)}
                        sx={{ width: '100px' }}
                      />
                    ) : (
                      item.SincronizadoIdEncabezado || ''
                    )}
                  </TableCell>
                  <TableCell>
                    {editingRows[item.Identificador] ? (
                      <IconButton 
                        color="primary" 
                        size="small"
                        onClick={() => saveRow(item, apiEndpoint, setter, currentData)}
                      >
                        <Save />
                      </IconButton>
                    ) : (
                      <IconButton 
                        color="primary" 
                        size="small"
                        onClick={() => toggleEditMode(item.Identificador)}
                        disabled={!tienePermiso('Editar Resultado Admin')}
                      >
                        <Edit />
                      </IconButton>
                    )}
                    <IconButton 
                      color="error" 
                      size="small"
                      onClick={() => deleteResult(item.Identificador, apiEndpoint, setter, currentData)}
                      disabled={!tienePermiso('Eliminar Resultado Admin')}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  No se encontraron resultados
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={getFilteredCount(currentData)}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    );
  };
  return (
    <div className="right_col" role="main">
      <div className="page-title">
        <div className="title_left">
          <h3>Administración de Resultados de Laboratorio</h3>
        </div>
      </div>
      <div className="clearfix"></div>

      <div className="row">
        <div className="col-md-12 col-sm-12">
          <div className="x_panel">
            <div className="x_title">
              <h2>Gestión de Tablas de Resultados <small>Edición de órdenes y estados</small></h2>
              <div className="clearfix"></div>
            </div>
            <div className="x_content">
              <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    aria-label="tabs de resultados"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {/* <Tab label="Resultados BT" /> */}
                    <Tab label="Resultados CM250" />
                    <Tab label="Resultados Cobas Pure" />
                    <Tab label="Resultados Hematología" />
                    {/* <Tab label="Resultados I400" /> */}
                  </Tabs>
                </Box>                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        margin="dense"
                        label="Buscar por ID Orden, Examen o Resultado"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button 
                        variant="contained" 
                        color="primary"
                        onClick={fetchData}
                        startIcon={<Search />}
                        sx={{ mr: 1 }}
                      >
                        Buscar
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="secondary"
                        onClick={() => {
                          setSearchTerm('');
                          fetchData();
                        }}
                      >
                        Limpiar Filtros
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid container sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Usuario currente: {user?.usuario || user?.codigoUsuario || 'No identificado'} | 
                        Sucursal: {sucursalSeleccionada?.nombreSucursal || 'No seleccionada'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* <TabPanel value={tabValue} index={0}>
                  {renderTable()}
                </TabPanel> */}
                <TabPanel value={tabValue} index={0}>
                  {renderTable()}
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  {renderTable()}
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                  {renderTable()}                </TabPanel>
                {/* <TabPanel value={tabValue} index={4}>
                  {renderTable()}
                </TabPanel> */}
              </Box>
            </div>
          </div>
        </div>
      </div>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );

};


export default AdministracionResultados;
